import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Save, Loader2, Send, Plus, Trash2, Download, FileText } from "lucide-react";
import { toast } from "sonner";
import RepuestoCombobox from "@/components/RepuestoCombobox";

import { logoColor as LOGO_URL } from "@/lib/logos";
const IVA_RATE = 0.19;
const fmtCLP = (n) => "$" + Math.round(Number(n) || 0).toLocaleString("es-CL");

export default function CotizacionEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { api } = useAuth();

  const [reportId, setReportId] = useState(id || null);
  const [folio, setFolio] = useState("");
  const [estado, setEstado] = useState("borrador");
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  const [clients, setClients] = useState([]);
  const [repuestos, setRepuestos] = useState([]);
  const [servicios, setServicios] = useState([]);

  const [cot, setCot] = useState({
    cliente_id: "", cliente_nombre: "", cliente_rut: "", contacto_nombre: "", contacto_email: "",
    sucursal: "", titulo: "Cotización de servicios y repuestos",
    items: [], descuento_pct: 0, incluye_iva: true, validez_dias: 15,
    condiciones: "", observaciones: "",
  });

  const uc = (f, v) => setCot((p) => ({ ...p, [f]: v }));

  useEffect(() => {
    api.get("/api/clients").then((r) => setClients(r.data)).catch(() => {});
    api.get("/api/repuestos").then((r) => setRepuestos(r.data)).catch(() => {});
    api.get("/api/servicios").then((r) => setServicios(r.data)).catch(() => {});
  }, [api]);

  useEffect(() => {
    if (!id) return;
    api.get(`/api/cotizaciones/${id}`).then((r) => {
      const d = r.data;
      setCot({
        cliente_id: d.cliente_id || "", cliente_nombre: d.cliente_nombre || "",
        cliente_rut: d.cliente_rut || "", contacto_nombre: d.contacto_nombre || "",
        contacto_email: d.contacto_email || "", sucursal: d.sucursal || "",
        titulo: d.titulo || "Cotización de servicios y repuestos",
        items: d.items || [], descuento_pct: d.descuento_pct || 0,
        incluye_iva: d.incluye_iva !== false, validez_dias: d.validez_dias || 15,
        condiciones: d.condiciones || "", observaciones: d.observaciones || "",
      });
      setFolio(d.folio); setReportId(d.id); setEstado(d.estado || "borrador");
    }).catch(() => toast.error("Error al cargar la cotización"));
  }, [id, api]);

  const selectClient = (clientId) => {
    const c = clients.find((x) => x.id === clientId);
    if (!c) return;
    setCot((p) => ({
      ...p, cliente_id: clientId, cliente_nombre: c.nombre, cliente_rut: c.rut || "",
      contacto_nombre: c.contacto_nombre || "", contacto_email: c.contacto_email || "", sucursal: "",
    }));
  };
  const selectedClient = clients.find((c) => c.id === cot.cliente_id);

  // --- Items ---
  const setItems = (next) => uc("items", next);
  const addRepuesto = (repId) => {
    const r = repuestos.find((x) => x.id === repId);
    if (!r) return;
    setItems([...cot.items, {
      tipo: "repuesto", ref_id: r.id, sku: r.sku, nombre: r.nombre,
      descripcion: "", cantidad: 1, precio_unitario: r.precio_venta || 0,
    }]);
  };
  const addServicio = (servId) => {
    const s = servicios.find((x) => x.id === servId);
    if (!s) return;
    setItems([...cot.items, {
      tipo: "servicio", ref_id: s.id, sku: "", nombre: s.nombre,
      descripcion: s.descripcion || "", cantidad: 1, precio_unitario: s.precio || 0,
    }]);
  };
  const addLibre = () => setItems([...cot.items, {
    tipo: "libre", ref_id: "", sku: "", nombre: "", descripcion: "", cantidad: 1, precio_unitario: 0,
  }]);
  const updateItem = (i, f, v) => {
    const n = [...cot.items];
    n[i] = { ...n[i], [f]: v };
    setItems(n);
  };
  const removeItem = (i) => setItems(cot.items.filter((_, idx) => idx !== i));

  // --- Totales ---
  const neto = cot.items.reduce((a, it) => a + (Number(it.cantidad) || 0) * (Number(it.precio_unitario) || 0), 0);
  const descuentoMonto = Math.round(neto * (Number(cot.descuento_pct) || 0) / 100);
  const base = neto - descuentoMonto;
  const iva = cot.incluye_iva ? Math.round(base * IVA_RATE) : 0;
  const total = base + iva;

  const payload = () => ({ ...cot, descuento_pct: Number(cot.descuento_pct) || 0,
    validez_dias: Number(cot.validez_dias) || 15 });

  const save = async ({ silent = false } = {}) => {
    if (!cot.cliente_nombre) { toast.error("Selecciona un cliente"); return null; }
    setSaving(true);
    try {
      if (reportId) {
        const r = await api.put(`/api/cotizaciones/${reportId}`, payload());
        if (!silent) toast.success("Cotización guardada");
        return r.data.id;
      }
      const r = await api.post("/api/cotizaciones", payload());
      setReportId(r.data.id); setFolio(r.data.folio);
      if (!silent) toast.success(`Cotización creada: ${r.data.folio}`);
      return r.data.id;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar");
      return null;
    } finally { setSaving(false); }
  };

  const send = async () => {
    if (!cot.cliente_nombre) { toast.error("Selecciona un cliente"); return; }
    if (cot.items.length === 0) { toast.error("Agrega al menos un ítem"); return; }
    setSending(true);
    try {
      const savedId = await save({ silent: true });
      if (!savedId) throw new Error("No se pudo guardar");
      const r = await api.post(`/api/cotizaciones/${savedId}/send`);
      toast.success(`Cotización ${r.data.folio} enviada`);
      if (r.data.correo_enviado) toast.success("Correo enviado al cliente");
      else if (r.data.email_error) toast.error(`Correo no enviado: ${r.data.email_error}`);
      navigate("/tecnico/cotizaciones");
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Error al enviar");
    } finally { setSending(false); }
  };

  const downloadPdf = async () => {
    const savedId = reportId || (await save({ silent: true }));
    if (!savedId) return;
    try {
      const r = await api.get(`/api/cotizaciones/${savedId}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `${folio || "cotizacion"}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Error al descargar PDF"); }
  };

  return (
    <div className="min-h-screen bg-[#F5F7FA]">
      <header className="bg-white border-b sticky top-0 z-30">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="" className="h-7" />
            <span className="text-sm font-semibold text-[#384091]">{folio || "Nueva Cotización"}</span>
            {estado === "enviada" && <Badge className="bg-emerald-50 text-emerald-700 border-0 text-[10px]">Enviada</Badge>}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => save()} disabled={saving || sending}>
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Save className="h-3.5 w-3.5 mr-1" />}
              Guardar
            </Button>
            <Button variant="ghost" size="sm" onClick={() => navigate("/tecnico/cotizaciones")}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-4">
        {/* Cliente */}
        <Card className="border-0 shadow-sm">
          <CardContent className="p-5 space-y-4">
            <h2 className="font-bold text-lg text-[#384091]">Datos del cliente</h2>
            <div>
              <Label>Cliente *</Label>
              <Select value={cot.cliente_id} onValueChange={selectClient}>
                <SelectTrigger className="mt-1 bg-white"><SelectValue placeholder="Seleccionar cliente..." /></SelectTrigger>
                <SelectContent className="bg-white">
                  {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {selectedClient?.direcciones?.length > 0 && (
              <div>
                <Label>Sucursal</Label>
                <Select value={cot.sucursal} onValueChange={(v) => uc("sucursal", v)}>
                  <SelectTrigger className="mt-1 bg-white"><SelectValue placeholder="Seleccionar (opcional)..." /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {selectedClient.direcciones.map((d) => (
                      <SelectItem key={d.nombre} value={d.nombre}>{d.nombre} — {d.direccion}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Contacto</Label>
                <Input value={cot.contacto_nombre} onChange={(e) => uc("contacto_nombre", e.target.value)} className="mt-1 bg-white" /></div>
              <div><Label>Correo</Label>
                <Input value={cot.contacto_email} onChange={(e) => uc("contacto_email", e.target.value)} className="mt-1 bg-white" /></div>
            </div>
            <div>
              <Label>Título de la cotización</Label>
              <Input value={cot.titulo} onChange={(e) => uc("titulo", e.target.value)} className="mt-1 bg-white" />
            </div>
          </CardContent>
        </Card>

        {/* Items */}
        <Card className="border-0 shadow-sm">
          <CardContent className="p-5 space-y-3">
            <h2 className="font-bold text-lg text-[#384091]">Ítems de la cotización</h2>

            {cot.items.length === 0 && (
              <p className="text-sm text-gray-400">Agrega repuestos, servicios o ítems libres.</p>
            )}

            {cot.items.map((it, i) => (
              <div key={i} className="border rounded-lg p-3 bg-gray-50/50 space-y-2">
                <div className="flex items-center justify-between">
                  <Badge className={
                    it.tipo === "repuesto" ? "bg-[#0FB4B7]/10 text-[#0D999C] border-0"
                    : it.tipo === "servicio" ? "bg-[#384091]/10 text-[#384091] border-0"
                    : "bg-gray-200 text-gray-600 border-0"}>
                    {it.tipo === "repuesto" ? "Repuesto" : it.tipo === "servicio" ? "Servicio" : "Ítem libre"}
                    {it.sku ? ` · ${it.sku}` : ""}
                  </Badge>
                  <button onClick={() => removeItem(i)} className="text-red-400 hover:text-red-600">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                <Input
                  value={it.nombre}
                  onChange={(e) => updateItem(i, "nombre", e.target.value)}
                  placeholder="Nombre / detalle del ítem"
                  className="bg-white"
                  disabled={it.tipo !== "libre"}
                />
                <Input
                  value={it.descripcion}
                  onChange={(e) => updateItem(i, "descripcion", e.target.value)}
                  placeholder="Descripción (opcional)"
                  className="bg-white text-sm"
                />
                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    <Label className="text-xs text-gray-500">Cantidad</Label>
                    <Input type="number" min="1" value={it.cantidad}
                      onChange={(e) => updateItem(i, "cantidad", parseInt(e.target.value) || 1)}
                      className="bg-white h-9 mt-0.5" />
                  </div>
                  <div className="flex-1">
                    <Label className="text-xs text-gray-500">Precio unitario</Label>
                    <Input type="number" min="0" value={it.precio_unitario}
                      onChange={(e) => updateItem(i, "precio_unitario", Number(e.target.value.replace(/\D/g, "")) || 0)}
                      className="bg-white h-9 mt-0.5" />
                  </div>
                  <div className="flex-1 text-right">
                    <Label className="text-xs text-gray-500">Subtotal</Label>
                    <p className="font-semibold text-[#384091] h-9 flex items-center justify-end">
                      {fmtCLP((Number(it.cantidad) || 0) * (Number(it.precio_unitario) || 0))}
                    </p>
                  </div>
                </div>
              </div>
            ))}

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 pt-1">
              <div>
                <RepuestoCombobox
                  catalogo={repuestos}
                  value=""
                  onSelect={addRepuesto}
                  placeholder="+ Repuesto del catálogo"
                />
              </div>
              <Select value="" onValueChange={addServicio}>
                <SelectTrigger className="bg-white h-9 text-sm"><SelectValue placeholder="+ Servicio" /></SelectTrigger>
                <SelectContent className="bg-white max-h-60">
                  {servicios.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" onClick={addLibre}
                className="border-dashed h-9">
                <Plus className="h-4 w-4 mr-1" /> Ítem libre
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Totales y condiciones */}
        <Card className="border-0 shadow-sm">
          <CardContent className="p-5 space-y-4">
            <h2 className="font-bold text-lg text-[#384091]">Resumen y condiciones</h2>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Descuento (%)</Label>
                <Input type="number" min="0" max="100" value={cot.descuento_pct}
                  onChange={(e) => uc("descuento_pct", Number(e.target.value.replace(/\D/g, "")) || 0)}
                  className="mt-1 bg-white" />
              </div>
              <div>
                <Label>Validez (días)</Label>
                <Input type="number" min="1" value={cot.validez_dias}
                  onChange={(e) => uc("validez_dias", Number(e.target.value.replace(/\D/g, "")) || 1)}
                  className="mt-1 bg-white" />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={cot.incluye_iva}
                onChange={(e) => uc("incluye_iva", e.target.checked)} className="h-4 w-4" />
              Incluir IVA (19%)
            </label>

            <div className="bg-gray-50 rounded-lg p-4 space-y-1.5 text-sm">
              <div className="flex justify-between"><span className="text-gray-500">Neto</span><span className="font-medium">{fmtCLP(neto)}</span></div>
              {descuentoMonto > 0 && (
                <div className="flex justify-between text-amber-700">
                  <span>Descuento ({cot.descuento_pct}%)</span><span>- {fmtCLP(descuentoMonto)}</span>
                </div>
              )}
              {cot.incluye_iva && (
                <div className="flex justify-between"><span className="text-gray-500">IVA (19%)</span><span className="font-medium">{fmtCLP(iva)}</span></div>
              )}
              <div className="flex justify-between border-t pt-1.5 mt-1.5 text-base">
                <span className="font-bold text-[#384091]">Total</span>
                <span className="font-bold text-[#384091]">{fmtCLP(total)}</span>
              </div>
            </div>

            <div>
              <Label>Condiciones comerciales</Label>
              <Textarea rows={3} value={cot.condiciones} onChange={(e) => uc("condiciones", e.target.value)}
                placeholder="Dejar vacío para usar las condiciones estándar de Odontofix." className="mt-1 bg-white" />
            </div>
            <div>
              <Label>Observaciones</Label>
              <Textarea rows={2} value={cot.observaciones} onChange={(e) => uc("observaciones", e.target.value)}
                className="mt-1 bg-white" />
            </div>
          </CardContent>
        </Card>

        <div className="flex flex-col sm:flex-row gap-2">
          <Button variant="outline" className="flex-1" onClick={downloadPdf} disabled={saving || sending}>
            <Download className="h-4 w-4 mr-1" /> Descargar PDF
          </Button>
          <Button className="flex-1 bg-[#384091] hover:bg-[#2D3375] text-white h-11"
            onClick={send} disabled={sending || saving}>
            {sending ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : <Send className="h-5 w-5 mr-2" />}
            Enviar al cliente
          </Button>
        </div>
      </main>
    </div>
  );
}
